import axios from "axios";

const BASE_URL = "https://api.open-meteo.com/v1/forecast";
const ELEVATION_URL = "https://api.open-meteo.com/v1/elevation";
const GEOCODING_URL = "https://geocoding-api.open-meteo.com/v1/search";

const openMeteoAxios = axios.create();

const DAILY_PARAMS = "relative_humidity_2m_min,relative_humidity_2m_max,temperature_2m_mean,temperature_2m_max,temperature_2m_min,sunrise,sunset,et0_fao_evapotranspiration";
const HOURLY_PARAMS = "temperature_2m,relative_humidity_2m,dew_point_2m,wind_speed_10m,wind_direction_10m,et0_fao_evapotranspiration,pressure_msl";
const CURRENT_PARAMS = "temperature_2m,wind_speed_10m,pressure_msl";

function calculateDaylightHours(sunriseStr, sunsetStr) {
    const sunrise = new Date(sunriseStr);
    const sunset = new Date(sunsetStr);
    return (sunset - sunrise) / (1000 * 60 * 60); // ms → hours
}

async function fetchElevation(latitude, longitude) {
    const res = await openMeteoAxios.get(ELEVATION_URL, { params: { latitude, longitude } });
    return res.data.elevation;
}

function getDayOfYear(dateStr) {
    const date = new Date(dateStr);
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date - start;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
}

export async function fetchWeatherDataFormatted({
                                                    latitude,
                                                    longitude,
                                                    name = "",
                                                    startDate = null,
                                                    endDate = null,
                                                    forecastDays = 1,
                                                }) {
    try {
        if (!latitude || !longitude) throw new Error("Latitude and longitude are required");

        const params = {
            latitude,
            longitude,
            timezone: "auto",
            models: "best_match",
            daily: DAILY_PARAMS,
            hourly: HOURLY_PARAMS,
            current: CURRENT_PARAMS,
        };

        let isInterval = false;

        if (startDate && endDate) {
            params.start_date = startDate;
            params.end_date = endDate;
            isInterval = true; // date range mode
        } else {
            params.forecast_days = forecastDays || 1; // fallback to 1 day
        }

        const forecastRes = await openMeteoAxios.get(BASE_URL, { params });
        const data = forecastRes.data;
        const { daily, current } = data;

        const location = {
            name,
            latitude,
            longitude,
            elevation: await fetchElevation(latitude, longitude),
            timezone: data.timezone,
            J: getDayOfYear(daily.time?.[0] || new Date()),
        };

        const Uz = +(current.wind_speed_10m / 3.6).toFixed(2); // m/s
        const P = +(current.pressure_msl / 10).toFixed(2); // kPa
        const zw = 10;

        let weather;

        if (!isInterval && forecastDays === 1) {
            // Single Day Mode
            const n = calculateDaylightHours(daily.sunrise[0], daily.sunset[0]);

            weather = {
                Tmax: daily.temperature_2m_max[0],
                Tmin: daily.temperature_2m_min[0],
                Ta: daily.temperature_2m_mean[0],
                RHmax: daily.relative_humidity_2m_max[0],
                RHmin: daily.relative_humidity_2m_min[0],
                Uz,
                zw,
                P,
                n: +n.toFixed(2),
                hc_ref: null,
                Rn: null,
                Rs: null,
            };
        } else {
            // Interval Mode
            weather = daily.time.map((date, idx) => ({
                date,
                Tmax: daily.temperature_2m_max[idx],
                Tmin: daily.temperature_2m_min[idx],
                Ta: daily.temperature_2m_mean[idx],
                RHmax: daily.relative_humidity_2m_max[idx],
                RHmin: daily.relative_humidity_2m_min[idx],
                Uz,
                zw,
                P,
                n: +calculateDaylightHours(daily.sunrise[idx], daily.sunset[idx]).toFixed(2),
                hc_ref: null,
                Rn: null,
                Rs: null,
            }));
        }

        return {
            location,
            weather,
            metadata: {
                generation_time_ms: data.generationtime_ms,
                data_source: "Open-Meteo API",
            },
        };
    } catch (error) {
        console.error("Error fetching weather data:", error.message);
        throw new Error("Failed to fetch weather data");
    }
}

export async function getWeatherData(city, { startDate = null, endDate = null, forecastDays = 1 } = {}) {
    try {
        const geoRes = await openMeteoAxios.get(GEOCODING_URL, { params: { name: city, count: 1 } });
        const result = geoRes.data.results?.[0];
        if (!result) throw new Error("City not found");

        return await fetchWeatherDataFormatted({
            latitude: result.latitude,
            longitude: result.longitude,
            name: result.name,
            startDate,
            endDate,
            forecastDays,
        });
    } catch (error) {
        console.error("Error fetching weather data for city:", error.message);
        throw new Error("Failed to fetch weather data for city");
    }
}

export { calculateDaylightHours, getDayOfYear, fetchElevation };
